import React from 'react'
import { useHistory } from 'react-router-dom'

// Utils
import { getItem, removeItem } from '@sketch/utils'

// Constants
import { localStorageKeys, routes } from '@sketch/constants'

// Components
import { Button } from '@sketch/components'
import DocumentHeader from 'components/DocumentHeader'
import { useToast } from '@sketch/toasts'
import {
  PrivateWorkspaceRoute,
  PrivateWorkspaceRouteExtraProps,
} from 'modules/workspace/containers'

// Local Components
import EmptyState from '../EmptyState'
import EmptyStateWithBanners from './EmptyStateWithBanners'

// GQL
import {
  GetWorkspacesDocument,
  useDeleteWorkspaceMutation,
  WorkspaceMinimalFragment,
  GetWorkspaceUploadsRestrictedForQuery,
} from '@sketch/gql-types'

// Operations

type UploadsRestrictedForType = GetWorkspaceUploadsRestrictedForQuery['workspace']['uploadsRestrictedFor']

interface ExpiredWorkspaceActionsProps {
  workspaceId: string
  canRenewSubscription: boolean
}

const ExpiredWorkspaceActions: React.FC<ExpiredWorkspaceActionsProps> = ({
  workspaceId,
  canRenewSubscription,
}) => {
  const history = useHistory()
  const { showToast } = useToast()

  const [deleteWorkspace, { loading }] = useDeleteWorkspaceMutation({
    variables: { workspaceId },
    onError: 'show-toast',
    onCompleted: data => {
      const { deletedWorkspace } = data.deleteWorkspace || {}

      // Remove last visited workspace from local storage
      const storedWorkspaceId = getItem(localStorageKeys.lastWorkspaceIdKey)

      if (storedWorkspaceId === deletedWorkspace?.identifier) {
        removeItem(localStorageKeys.lastWorkspaceIdKey)
      }

      showToast('Your workspace was successfully deleted.', 'positive')
      history.push(routes.ENTRY.create({}))
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetWorkspacesDocument }],
  })

  return (
    <>
      <Button
        variant="negative-secondary"
        onClick={() => deleteWorkspace()}
        loading={loading}
      >
        Delete Workspace…
      </Button>
      {canRenewSubscription ? (
        <Button
          variant="primary"
          onClick={() =>
            history.push(
              routes.WORKSPACE_SUBSCRIBE.create({
                workspaceId,
              })
            )
          }
          disabled={loading}
        >
          Renew Subscription…
        </Button>
      ) : null}
    </>
  )
}

interface ShareEmptyStateProps {
  filter: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | 'TEMPLATE' | null
  search?: string
  workspace: WorkspaceMinimalFragment
  workspaceHasDocuments?: boolean | null
  uploadsRestrictedFor: UploadsRestrictedForType
}

export const ShareEmptyState: React.FC<ShareEmptyStateProps> = ({
  filter,
  search,
  workspace,
  workspaceHasDocuments,
  uploadsRestrictedFor,
}) => {
  return (
    <PrivateWorkspaceRoute
      render={({ workspace }: PrivateWorkspaceRouteExtraProps) => {
        const { identifier, status, userCanEdit, userRole } = workspace

        if (status === 'INACTIVE' && [null, 'NO_PROJECT'].includes(filter)) {
          return (
            <EmptyState
              icon="workspace"
              title="Cannot view this Workspace"
              description={
                <>
                  Renew your subscription to activate the Workspace again.
                  <br />
                  Alternatively, you can delete the Workspace if no longer need
                  it.
                  <br />
                </>
              }
              callToAction={
                <ExpiredWorkspaceActions
                  workspaceId={identifier}
                  canRenewSubscription={
                    userRole === 'ADMIN' || userRole === 'FINANCE'
                  }
                />
              }
            />
          )
        }

        if (filter === null) {
          if (
            !search &&
            !workspaceHasDocuments &&
            uploadsRestrictedFor === 'NONE'
          ) {
            return (
              <>
                <DocumentHeader
                  title="All Documents"
                  description="All the documents in your Workspace — in sync and available anywhere."
                />
                <EmptyStateWithBanners workspace={workspace} />
              </>
            )
          } else {
            return (
              <EmptyState
                search={search}
                title="Your Workspace hasn’t created anything yet"
                description="Create and upload a document in Sketch to see it here."
                icon="document"
              />
            )
          }
        }

        if (userCanEdit && filter === 'NO_PROJECT') {
          return (
            <EmptyState
              search={search}
              title="Nothing to see here"
              description="There are no documents to show here."
              icon="document"
            />
          )
        }

        if (userRole === 'GUEST' && filter === 'LIBRARY') {
          return (
            <EmptyState
              search={search}
              title="No Libraries"
              description="No Libraries added yet."
              icon="link"
            />
          )
        }

        if (filter === 'LIBRARY') {
          return (
            <EmptyState
              search={search}
              title="Your Workspace hasn’t added any Libraries yet"
              description="Any documents with “Use as Library” checked in Document Settings will appear here."
              icon="link"
            />
          )
        }

        if (filter === 'SHARED_WITH_ME') {
          return (
            <EmptyState
              search={search}
              title="No shared documents"
              description="No documents shared with you yet."
              icon="person-circle"
            />
          )
        }

        if (filter === 'TEMPLATE') {
          return (
            <EmptyState
              search={search}
              title="No templates"
              description="No templates added to Workspace yet."
              icon="template"
            />
          )
        }

        return (
          <EmptyState
            search={search}
            title="Your Workspace hasn’t created anything yet"
            description="Only Editors can create documents."
            icon="document"
          />
        )
      }}
    />
  )
}

export default ShareEmptyState
